import {CHANGE} from '@megaphonenpm/mputils/src/constants.js';
import {get} from 'lodash-es';
import {GetWebPollTask} from './GetWebPollTask.js';
import {WebPollService} from './WebPollService.js';
import {SCENE_TYPE_PREDICTION, SCENE_TYPE_SURVEY} from './constants.js';
import {areJSONEqual} from '@megaphonenpm/mputils/src/utils.js';

/**
 * Retrieves the specified webpoll or prediction and compares it to the local version.
 * If the two webpolls or predictions are different, then UpdateWebPollTask dispatches a CHANGE event.
 */
export class UpdateWebPollTask extends GetWebPollTask {
  constructor (sourceID, dataStore, sceneType = SCENE_TYPE_SURVEY) {
    super(sourceID, dataStore, sceneType);
    this.name = 'UPDATE_WEBPOLL_TASK';
    this.dataStore = dataStore;
    this.localWebPollService = new WebPollService();
  }

  /**
   * If the loaded webpoll or prediction differs from the web poll currently in the dataStore, then
   * dispatch a CHANGE event.
   *
   * @param loadedRecord The content of the loaded webpoll or prediction, and the current
   *                     results (votes).
   */
  onWebPollLoadSuccess (loadedRecord) {
    let loadedWebPollID = null;
    switch (this.sceneType) {
      case SCENE_TYPE_SURVEY:
        // In the case of webpolls, the loaded poll record's id belongs to the data object, so
        // retrieve it directly via loadedRecord[this.getContentFieldName()].id.
        loadedWebPollID = get(loadedRecord, `${this.getContentFieldName()}.id`);
        break;
      case SCENE_TYPE_PREDICTION:
        // In the case of predictions, the loaded prediction record does not have its own id.
        // Instead, the "prediction" retrieved is actually a scene in a "prediction group"
        // (analogous to a playlist), so use that scene's id to identify the prediction in the
        // client-side datastore.
        loadedWebPollID = get(loadedRecord, `${this.getContentFieldName()}.scenes[0].id`);
        break;
    }

    let loadedWebPollUpdatedAt = get(loadedRecord, `${this.getContentFieldName()}.updated.at`);
    let loadedWebPollResults = get(loadedRecord, 'poll_results');
    let loadedWebPollState = get(loadedRecord, `${this.getContentFieldName()}.state`);

    let currentWebPollRecord = this.dataStore.webpolls.get(this.sourceID);
    let currentWebPollUpdatedAt = get(currentWebPollRecord, `${this.getContentFieldName()}.updated.at`)
    let currentWebPollResults = get(currentWebPollRecord, 'poll_results');
    let currentWebPollState = get(currentWebPollRecord, `${this.getContentFieldName()}.state`);

    let webpollChanged = currentWebPollUpdatedAt !== loadedWebPollUpdatedAt;
    let webpollStateChanged = currentWebPollState !== loadedWebPollState;
    let webpollResultsChanged = !areJSONEqual(loadedWebPollResults, currentWebPollResults);

    // If either the web poll's content OR current vote results have changed, update the dataStore.
    // Note that UpdateWebPollTask is responsible for keeping the dataStore updated ONLY; it does
    // NOT update the AppState. AppState updates are handled by WebPollAppStateSynchronizer.
    if (webpollChanged || webpollStateChanged || webpollResultsChanged) {
      this.dataStore.webpolls.set(loadedWebPollID, loadedRecord);

      this.dispatchEvent(CHANGE, {
        loadedWebPollID: loadedWebPollID,
        currentUpdatedAt: currentWebPollUpdatedAt,
        newUpdatedAt: loadedWebPollUpdatedAt,
        currentState: currentWebPollState,
        newState: loadedWebPollState,
        currentResults: currentWebPollResults,
        newResults: loadedWebPollResults
      });
    }
  }

  getContentFieldName () {
    return this.localWebPollService.getContentFieldName(this.sceneType);
  }

  getResultsFieldName () {
    return this.localWebPollService.getResultsFieldName(this.sceneType);
  }
}
